import { useRef, useEffect, useState, useCallback } from "react"
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"
import "leaflet/dist/leaflet.css"
// import NavigationIcon from "@mui/icons-material/Navigation"
import CellTowerIcon from '@mui/icons-material/CellTower';
import L from "leaflet"
import "leaflet-rotatedmarker"
import { renderToStaticMarkup } from "react-dom/server"
import { Typography, Box, CircularProgress } from "@mui/material"
import api from "../services/api"
import { API_ENDPOINTS } from "../config/api"
import { useMultipleWebSocket } from "../context/MultipleWebSocketContext";
import COUNTRY_CODE_MAP from '../config/countryFlags';
import { ReactComponent as TowerIcon } from '../assets/icon/tower.svg';
import { getShipColor } from '../config/shipConstants';

// Function to create a Leaflet icon from an MUI icon component or SVG
const createIcon = (IconComponent, shipType) => {
  const iconMarkup = renderToStaticMarkup(
    IconComponent === CellTowerIcon ? 
    <TowerIcon style={{ width: "35px", height: "35px" }} /> :
    <svg height="32" width="32">
      <polygon 
        fill={getShipColor(shipType)} 
        points="12,14 12,28 16,24 20,28 20,14 16,4"
      />
    </svg>
  )
  return L.divIcon({
    html: iconMarkup,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  })
}

const getCountryFlag = (country) => {
  try {
    if (!country) return "🏳️"; // Default flag if country is undefined

    const countryCode = COUNTRY_CODE_MAP[country] || Object.keys(COUNTRY_CODE_MAP).find(
      key => key.toLowerCase() === country.toLowerCase()
    );

    if (!countryCode) {
      console.warn(`No country code found for: ${country}`);
      return "🏳️"; // Default flag if not found
    }

    return String.fromCodePoint(...[...countryCode].map(c => 127397 + c.charCodeAt(0)));
  } catch (e) {
    console.error(`Error getting flag for country: ${country}`, e);
    return "🏳️"; // Default flag on error
  }
};

const MarkerWithRotation = ({ position, type, heading, ...props }) => {
  const markerRef = useRef(null)

  useEffect(() => {
    if (markerRef.current && heading) {
      markerRef.current.setRotationAngle(heading)
    }
  }, [heading])

  const icon = type === "tower" ? createIcon(CellTowerIcon, type) : createIcon("ship", type)
  return (
    <Marker ref={markerRef} position={position} icon={icon} {...props}>
      <Popup>
        <div>
          {type === "tower" ? (
            <>
              <h3>{props.name || ""}</h3>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Latitude:</strong> {position[0]}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Longitude:</strong> {position[1]}</p>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Name:</strong> {props.name || "Unknown"}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>CallSign:</strong> {props.callsign || "N/A"}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Country:</strong> {getCountryFlag(props.country)} ({props.country || "Unknown"})</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>IMO:</strong> {props.imo || "N/A"}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>MMSI:</strong> {props.id || "N/A"}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Ship Type:</strong> {type || "N/A"}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Navigation Status:</strong> {props.navigationStatus || "N/A"}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>COG:</strong> {heading || "-"}°</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>SOG:</strong> {props.sog || "-"}°</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Latitude:</strong> {position[0]}</p>
              <p style={{ marginTop: 0, marginBottom: 2 }}><strong>Longitude:</strong> {position[1]}</p>
            </>
          )}
        </div>
      </Popup>
    </Marker>
  )
}

const AISMonitoring = () => {
  const [markers, setMarkers] = useState([])
  const [centers, setCenters] = useState([0, 115])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [vesselCache, setVesselCache] = useState({})
  const [sensors, setSensors] = useState([])
  const { connect, getSocket } = useMultipleWebSocket();
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [zoom, setZoom] = useState(10)
  
  const fetchAISSensors = useCallback(async () => {
    try {
      const sensorsResponse = await api.get(`${API_ENDPOINTS.SENSOR.GET_ALL}?filter=${encodeURIComponent(JSON.stringify({"url": {"$regex": "^ws"}}))}&isPreview=true`);
      let aisSensors = sensorsResponse.data.sensors;

      if (aisSensors && aisSensors.length > 0) {
        setSensors(aisSensors.filter((sensor) => sensor.is_active));

        let activeSensor = aisSensors.find((sensor) => sensor.is_active);
        if (activeSensor) {
          const clientInfo = JSON.parse(localStorage.getItem("clientInfo"));
          const clientId = clientInfo.id;
          if (!clientId) {
            throw new Error("Client ID not found");
          }
          if (activeSensor.latitude && activeSensor.longitude) {
            function toRadians(deg) {
              return deg * Math.PI / 180;
            }
            
            function toDegrees(rad) {
              return rad * 180 / Math.PI;
            }
            
            function getCentroid(coords) {
              let x = 0, y = 0, z = 0;
            
              coords.forEach(coord => {
                const lat = toRadians(coord.lat);
                const lon = toRadians(coord.lng);
            
                x += Math.cos(lat) * Math.cos(lon);
                y += Math.cos(lat) * Math.sin(lon);
                z += Math.sin(lat);
              });
            
              const total = coords.length;
              x /= total;
              y /= total;
              z /= total;
            
              const hyp = Math.sqrt(x * x + y * y);
              const lat = Math.atan2(z, hyp);
              const lon = Math.atan2(y, x);
            
              return {
                lat: toDegrees(lat),
                lng: toDegrees(lon)
              };
            }
            const centeroid = getCentroid(aisSensors.map((sensor) => ({ lat: sensor.latitude, lng: sensor.longitude }))); // Get centeroid
            if (aisSensors.length > 2) {
              setZoom(7);
            }
            setCenters([centeroid.lat, centeroid.lng]);
            setMarkers(
              aisSensors.map((sensor) => ({
                id: sensor.id,
                position: [sensor.latitude, sensor.longitude],
                heading: 0,
                shipType: "tower",
                name: sensor.name,
              }))
            );
          } else {
            setCenters([-2.6000285, 123.015776]);
          }
        } else {
          setError("No active AIS sensor found");
        }
      } else {
        setError("No AIS sensors found");
      }
    } catch (err) {
      console.error("Error fetching AIS sensors:", err);
      setError("Failed to fetch AIS sensors");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVesselData = useCallback(async (clientId, mmsi, forceRefresh = false) => {
    // Skip if MMSI is undefined or null
    if (!mmsi) {
      console.warn('Skipping vessel data fetch - no MMSI provided');
      return { vessel: {} };
    }

    try {
      // Check cache first, unless forceRefresh is true
      if (!forceRefresh && vesselCache[mmsi]) {
        return vesselCache[mmsi];
      }

      // Fetch vessel data by MMSI
      const vesselResponse = await api.get(API_ENDPOINTS.VESSEL.GET_ONE_BY_MMSI_AND_CLIENTID(mmsi, clientId));
      const vesselData = vesselResponse.data || {};
      
      // Update cache
      setVesselCache(prev => ({
        ...prev,
        [mmsi]: vesselData
      }));
      
      return vesselData;
    } catch (err) {
      console.error("Error fetching vessel data:", err);
      return { vessel: {} };
    }
  }, [vesselCache]);

  useEffect(() => {
    fetchAISSensors()
  }, [fetchAISSensors])

  useEffect(() => {
    sensors.forEach((sensor) => {
      connect(sensor.url);

      const socket = getSocket(sensor.url);
      if (socket) {
        setWsStatus("connected");
        socket.onmessage = async (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Skip processing if MMSI is missing
            if (!data.MMSI) {
              console.warn('Received WebSocket message without MMSI:', data);
              return;
            }
            
            // Validate required fields
            if (!data.Latitude || !data.Longitude) {
              console.warn('Skipping invalid AIS data:', {
                mmsi: data.MMSI,
                lat: data.Latitude,
                lon: data.Longitude
              });
              return;
            }
    
            // Get vessel info from cache or API
            let vesselInfo = { vessel: {} };
            try {
              vesselInfo = await fetchVesselData(sensor.client_id?.id, data.MMSI);
            } catch (err) {
              console.warn(`Failed to fetch vessel data for MMSI ${data.MMSI}:`, err);
              // Continue with basic data even if vessel fetch fails
            }
    
            const vesselData = vesselInfo.vessel ?? {};
    
            // Create a new marker object from the data
            const newMarker = {
              id: data.MMSI,
              position: [data.Latitude, data.Longitude],
              heading: data.Cog || 0,
              sog: vesselData.Sog || 0,
              name: vesselData.Name || data.Name || "",
              shipType: vesselData.ShipType || data.ShipType || "Unknown Vessel Type",
              country: vesselData.Country || data.Country || "Unknown",
              callsign: vesselData.CallSign || "",
              imo: vesselData.ImoNumber || "",
              navigationStatus: vesselData.NavigationStatusText || data.NavigationStatus || "Unknown",
            };
    
            setMarkers(prevMarkers => {
              const existingMarkerIndex = prevMarkers.findIndex(marker => marker.id === newMarker.id);
    
              if (existingMarkerIndex !== -1) {
                // Update existing marker
                const updatedMarkers = [...prevMarkers];
                const existingMarker = prevMarkers[existingMarkerIndex];
                
                // Only update name if new name is valid
                const updatedName = newMarker.name && newMarker.name !== "Unknown" 
                  ? newMarker.name 
                  : existingMarker.name;
    
                updatedMarkers[existingMarkerIndex] = {
                  ...existingMarker,
                  ...newMarker,
                  name: updatedName
                };
                return updatedMarkers;
              } else {
                // Add new marker
                return [...prevMarkers, newMarker];
              }
            });
    
            setCenters([data.Latitude, data.Longitude]);
          } catch (err) {
            console.error("Error processing WebSocket message:", err);
          }
        };
      }
    });
  }, [sensors, connect, getSocket, fetchVesselData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: "100%", width: "100%", paddingRight: { xs: 2, md: 5 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          AIS Monitoring
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: wsStatus === "connected" ? "success.main" : wsStatus === "error" ? "error.main" : "warning.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {wsStatus === "connected" ? "● Connected" : wsStatus === "error" ? "● Connection Error" : "● Disconnected"}
        </Typography>
      </Box>

      {error && (
        <Typography
          color="error"
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "error.light",
            borderRadius: 1,
            color: "error.contrastText",
          }}
        >
          {error}
        </Typography>
      )}

      <MapContainer center={centers} zoom={zoom} style={{ height: "90%", width: "100%", borderRadius: 10 }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        
        {markers.map((marker) => (
          <MarkerWithRotation
            key={marker.id}
            id={marker.id}
            position={marker.position}
            heading={marker.heading}
            type={marker.shipType}
            country={marker.country}
            name={marker.name}
            callsign={marker.callsign}
            imo={marker.imo}
            sog={marker.sog}
            navigationStatus={marker.navigationStatus}
          />
        ))}
      </MapContainer>
    </Box>
  )
}

export default AISMonitoring
